import { Modal, Spin, Tabs } from "antd";
import React, { useEffect, useRef, useState } from "react";
import apiFactory from "../../api";
import { CHATTING } from "../../config/Constant";
import { useInfoUser } from "../../store/UserStore";
import { openNotificationError } from "../../utils/Utils";
import { BasicMemberInfo } from "../select/directory/BasicMemberInfo";

const MemberReadUnreadModal = ({
  conversationId,
  conversationType,
  messageId,
  onCancel,
  isOpen,
}) => {
  const { user, languageMap } = useInfoUser();
  const [tabs, setTabs] = useState([]);
  const [memberSearch, setMemberSearch] = useState({
    search: {
      conversationId: conversationId,
      conversationType: conversationType,
      messageId: messageId,
      viewType: "ALL",
    },
    ts: new Date(),
    page: 1,
    limit: CHATTING.MEMBER_LIMIT,
  });
  const [memberList, setMemberList] = useState([]);
  const [activeKey, setActiveKey] = useState("ALL");
  const lastObserver = useRef();
  const [isLoading, setIsLoading] = useState(false);
  const [isMoreMember, setIsMoreMember] = useState(false);
  const [isLoadingFetchMember, setIsLoadingFetchMember] = useState(false);

  const changeTab = (key) => {
    setActiveKey(key);
    setMemberList([]);
    setMemberSearch((prevSearch) => ({
      ...prevSearch,
      search: {
        conversationId: conversationId,
        conversationType: conversationType,
        messageId: messageId,
        viewType: key,
      },
      page: 1,
    }));
  };

  const fetchMembersData = async () => {
    setIsLoadingFetchMember(true);

    try {
      const result =
        await apiFactory.userConversationApi.getMembersReadUnread(memberSearch);

      if (result?.status !== 200) {
        openNotificationError(result?.message);
        return;
      }

      setMemberList([...memberList, ...result?.data?.items]);
      setIsLoading(false);

      setIsMoreMember(result?.data?.items?.length === CHATTING.MEMBER_LIMIT);
    } finally {
      setIsLoadingFetchMember(false);
    }
  };

  const lastMemberRef = (node) => {
    if (isLoading || !isMoreMember) return;
    if (lastObserver.current) lastObserver.current.disconnect();

    lastObserver.current = new IntersectionObserver(async (entries) => {
      if (entries[0].isIntersecting) {
        setIsLoading(true);
        setMemberSearch((prevState) => {
          return {
            ...prevState,
            page: prevState.page + 1,
          };
        });
      }
    });

    if (node) lastObserver.current.observe(node);
  };

  useEffect(() => {
    fetchMembersData();
  }, [memberSearch]);

  const generateTabs = () => {
    return (
      <>
        {memberList?.length > 0 ? (
          <div className="h-[60vh] overflow-y-scroll">
            {memberList?.map((member, index) => {
              if (member?.organizations?.[0]) {
                member.organizations[0].avatar = member?.avatar || null;
              }
              return (
                <div
                  key={member?.userId}
                  ref={index === memberList?.length - 1 ? lastMemberRef : null}
                >
                  <BasicMemberInfo
                    languageMap={languageMap}
                    memberGroup={member}
                    screenInfo={"READ_UNREAD"}
                    isHost={member?.role === "HOST"}
                    isAdmin={member?.role === "ADMIN"}
                    isMe={member?.userId === user?.userId}
                    hasRead={member?.hasRead}
                    lastSeenTs={member?.lastSeenTs}
                    hasOrganizations={true}
                  />
                </div>
              );
            })}
          </div>
        ) : !isLoadingFetchMember ? (
          <div className="h-[60vh] flex items-center justify-center">
            <div>
              <span>No data found</span>
            </div>
          </div>
        ) : (
          <div className="h-[60vh] flex items-center justify-center">
            <Spin />
          </div>
        )}
      </>
    );
  };

  useEffect(() => {
    const tabsContent = generateTabs();
    const items = [
      {
        key: "ALL",
        label: languageMap?.["modal.seen.all"] ?? "All",

        children: tabsContent,
      },
      {
        key: "READ",
        label: languageMap?.["modal.seen.read"] ?? "Read",

        children: tabsContent,
      },
      {
        key: "UNREAD",
        label: languageMap?.["modal.seen.unread"] ?? "Unread",

        children: tabsContent,
      },
    ];
    setTabs(items);
  }, [memberList, languageMap, isLoadingFetchMember]);

  return (
    <Modal
      title={
        languageMap?.["modal.seen.title"] ?? "Participant message view status"
      }
      open={isOpen}
      onCancel={onCancel}
      width="800px"
      footer={false}
      centered
    >
      <Tabs
        defaultActiveKey="ALL"
        activeKey={activeKey}
        items={tabs}
        onChange={changeTab}
      />
    </Modal>
  );
};

export default React.memo(MemberReadUnreadModal);
